// ─────────────────────────────────────────────
//  TimeCombo — a search-as-you-type time picker (autocomplete).
//
//  Drop-in replacement for a native <select> of "HH:MM" options: the user can
//  click to see the full list OR start typing to filter it in real time.
//  Matching is digit-aware, so "7", "07", "715" and "07:15" all narrow to
//  07:15. Keyboard: ↑/↓ move, Enter selects, Esc closes. Blurring commits the
//  best match (or reverts to the previous value if the text matches nothing).
//
//  Controlled: value is the committed "HH:MM" string ("" = none); onChange fires
//  with the new committed value.
// ─────────────────────────────────────────────
import React, { useState, useRef, useEffect, useId } from "react";
import tokens from "../theme/tokens";

const { color, font, radius, space } = tokens;

const digits = (s) => String(s || "").replace(/\D/g, "");

// Does a "HH:MM" option match what the user typed? Rules that read naturally for
// times: a literal substring of the display ("07:3" → 07:30); 1-2 digits = the
// HOUR ("7"/"07" → 07:xx, "14" → 14:xx, but not 11:45); 3-4 digits = the exact
// zero-padded time ("715"/"0715" → 07:15).
// "5 PM" / "5pm" / "5:30 p.m." / "12 AM" → minutes since midnight, or null.
//
// ⚠️ THIS EXISTS BECAUSE THE FIELD LOOKED BROKEN. Every query containing a
// non-digit fell through to a literal substring search below, so a clinician
// typing "5 PM" — the way the whole hospital says it — got "No matching time"
// and reasonably concluded the board would not let them add a late case. The
// options are 24-hour and stay that way; only what is ACCEPTED widens.
//
// Deliberately NOT extended to a bare "5": with no am/pm that is genuinely
// ambiguous, and the morning reading is the common one for a start time. Better
// to keep "5" meaning 05:xx, as it always has, than to guess an OR time twelve
// hours wrong.
export function parseMeridiem(query) {
  const m = /^(\d{1,2})(?::?(\d{2}))?\s*([ap])\.?\s*m\.?$/i.exec(String(query || "").trim());
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = m[2] == null ? null : parseInt(m[2], 10);
  if (h < 1 || h > 12) return null;
  if (min != null && (min < 0 || min > 59)) return null;
  const pm = m[3].toLowerCase() === "p";
  if (h === 12) h = 0;            // 12 AM = 00, 12 PM = 12 (after the +12 below)
  if (pm) h += 12;
  return { hour: h, minute: min };
}

export function matchesTime(option, query) {
  const q = String(query || "").trim();
  if (!q) return true;
  // 12-hour input first: it contains letters and would otherwise be swallowed by
  // the literal-substring rule below and match nothing.
  const mer = parseMeridiem(q);
  if (mer) {
    const od = digits(option);
    const oh = parseInt(od.slice(0, 2), 10);
    if (oh !== mer.hour) return false;
    // No minutes typed = the whole hour, mirroring how "17" behaves.
    return mer.minute == null || parseInt(od.slice(2, 4), 10) === mer.minute;
  }
  // A query with a non-digit (a colon, e.g. "07:3", ":15") is a literal substring
  // search of the display. A purely-numeric query uses time rules ONLY — so "7"
  // means hour 7 (07:xx), not the "7" inside "17:00".
  if (/\D/.test(q)) return option.includes(q);
  const qd = digits(q);
  if (!qd) return false;
  const od = digits(option); // "07:15" → "0715"
  if (qd.length <= 2) return parseInt(od.slice(0, 2), 10) === parseInt(qd, 10);
  return od === qd.padStart(4, "0");
}

export default function TimeCombo({
  value = "",
  onChange,
  options = [],
  placeholder = "—",
  ariaLabel = "Time",
  disabled = false,
  inputStyle,
  autoFocus = false,
  // Lets a visible <label htmlFor> point at the real input. Without it a caller
  // can only name this control with ariaLabel, and a sibling <label> is a
  // promise to nobody.
  id,
}) {
  const [open, setOpen] = useState(false);
  // ⚠️ THE LIST NEEDS AN ID BECAUSE THE INPUT HAS TO POINT AT IT. A
  // role="combobox" is a PROMISE that aria-controls names the popup and
  // aria-activedescendant names the row the user is on. Without them a screen
  // reader announces "combobox", the user arrows down, the highlight moves on
  // screen — and nothing is spoken, because focus never leaves the input. The
  // control looked keyboard-accessible and was silent. useId keeps the ids
  // unique when several TimeCombos share a row (start AND end on one case).
  const uid = useId();
  const listId = `${uid}-listbox`;
  const optionId = (i) => `${uid}-option-${i}`;
  const [query, setQuery] = useState("");
  // ⚠️ HAS THE USER ACTUALLY TYPED SOMETHING YET?
  // Opening the list seeds `query` with the CURRENT value so the field keeps
  // showing what it holds. Filtering on that seed collapsed the menu to a single
  // option — the value already selected — so clicking the field offered the one
  // time it was already set to, with no way to see the others and no hint that
  // the text had to be deleted first. Reported live as "I can't change the
  // time". Until a keystroke, the seed is a LABEL, not a filter.
  const [typed, setTyped] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const blurTimer = useRef(null);

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const q = query.trim();
  const filtered = !q || !typed ? options : options.filter((o) => matchesTime(o, q));

  // Best committed match for typed text: exact string, else the first option
  // that matches the same rules the list filters by (so "7" → 07:00, "0730" →
  // 07:30). Empty text clears the value; no match → caller reverts.
  const bestMatch = (text) => {
    const t = text.trim();
    if (t === "") return "";
    if (options.includes(t)) return t;
    return options.find((o) => matchesTime(o, t)) || null;
  };

  const select = (opt) => {
    clearTimeout(blurTimer.current);
    onChange(opt);
    setQuery(opt);
    setOpen(false);
    setActive(-1);
  };

  const commit = (text) => {
    const m = bestMatch(text);
    if (m === "") onChange("");
    else if (m) onChange(m);
    // m === null → invalid: leave value unchanged; display reverts on close.
  };

  const openList = () => {
    if (disabled) return;
    // ⚠️ CANCEL ANY PENDING CLOSE. `onBlur` schedules a commit-and-close 120ms
    // later so a click on an option can land first — but nothing called off that
    // timer when focus CAME BACK. A real mouse click on a combobox routinely
    // blurs and refocuses, so the list opened and then shut itself about a tenth
    // of a second later: "when i open the time, close very fast, dont let me
    // choose anything". The timer must die the moment the field is live again.
    clearTimeout(blurTimer.current);
    setQuery(value || "");
    setTyped(false);
    setOpen(true);
    setActive(-1);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) { openList(); return; }
      setActive((i) => Math.min((i < 0 ? -1 : i) + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && active >= 0 && filtered[active]) select(filtered[active]);
      else { commit(query); setOpen(false); }
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery(value || "");
      setActive(-1);
    }
  };

  // Keep the active row scrolled into view.
  useEffect(() => {
    if (!open || active < 0 || !listRef.current) return;
    const el = listRef.current.children[active];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  return (
    <div ref={wrapRef} style={s.wrap}>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        // Only while a row is actually active: pointing at nothing is worse
        // than pointing at nowhere, because a stale id is announced as missing.
        aria-activedescendant={open && active >= 0 && filtered[active] ? optionId(active) : undefined}
        aria-label={ariaLabel}
        autoComplete="off"
        autoFocus={autoFocus}
        disabled={disabled}
        placeholder={placeholder}
        value={open ? query : (value || "")}
        style={{ ...s.input, ...inputStyle }}
        onFocus={openList}
        onClick={openList}
        onChange={(e) => { setQuery(e.target.value); setTyped(true); setOpen(true); setActive(0); }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          blurTimer.current = setTimeout(() => { commit(query); setOpen(false); setActive(-1); }, 120);
        }}
      />
      {open && (
        <ul ref={listRef} id={listId} style={s.list} role="listbox">
          {filtered.length === 0 ? (
            <li style={s.empty}>No matching time</li>
          ) : (
            filtered.map((o, i) => (
              <li
                key={o}
                id={optionId(i)}
                role="option"
                aria-selected={o === value}
                // onMouseDown (not onClick) so it fires before the input blur.
                onMouseDown={(e) => { e.preventDefault(); select(o); }}
                onMouseEnter={() => setActive(i)}
                style={{
                  ...s.option,
                  ...(i === active ? s.optionActive : {}),
                  ...(o === value ? s.optionSelected : {}),
                }}
              >
                {o}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

const s = {
  wrap: { position: "relative", width: "100%" },
  input: {
    width: "100%", boxSizing: "border-box",
    background: color.surface.raised, border: `1px solid ${color.border.default}`,
    borderRadius: radius.md, padding: "10px 12px", fontSize: font.size.body,
    fontFamily: "inherit", color: color.text.primary, fontVariantNumeric: "tabular-nums",
  },
  list: {
    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50,
    margin: 0, padding: 4, listStyle: "none",
    background: color.surface.card, border: `1px solid ${color.border.default}`,
    borderRadius: radius.md, boxShadow: tokens.shadow.md,
    maxHeight: 220, overflowY: "auto",
  },
  option: {
    padding: "7px 10px", fontSize: font.size.body, color: color.text.primary,
    borderRadius: radius.sm, cursor: "pointer", fontVariantNumeric: "tabular-nums",
  },
  optionActive: { background: color.surface.raised },
  optionSelected: { fontWeight: font.weight.bold, color: color.accent.primary },
  empty: { padding: "7px 10px", fontSize: font.size.label, color: color.text.muted },
};
